import styled, { css } from 'styled-components';

import { mediaQueries } from '@ui-component-library/styles/breakpoints';
import { TYPOGRAPHY_RESET } from '@ui-component-library/styles/components.reset';

import { COLORS, type ColorToken } from '../styles/colors';

import type { TypographyVariant } from './Typography.types';

const FONT_FAMILY_NISTA_GROTESK = "'Bw Nista Grotesk', sans-serif";

const DEFAULT_TYPOGRAPHY_STYLES = css`
  cursor: inherit;
`;

export const TYPOGRAPHY = {
  /* For high-impact hero sections and large call-to-actions.*/
  displayAndHeadlineXtraLarge: css`
    font-family: ${FONT_FAMILY_NISTA_GROTESK};
    font-family: fantasy;
    font-weight: 500;

    ${mediaQueries.mobile} {
      font-size: 42px;
      line-height: 48px;
      letter-spacing: -1px;
    }
    ${mediaQueries.tablet} {
      font-size: 48px;
      line-height: 56px;
      letter-spacing: -1.7px;
    }
    ${mediaQueries.desktop} {
      font-size: 56px;
      line-height: 64px;
      letter-spacing: -1.9px;
    }
  `,

  /* Secondary For high-impact hero sections and large call-to-actions. */
  displayAndHeadlineLarge: css`
    font-family: ${FONT_FAMILY_NISTA_GROTESK};
    font-weight: 500;

    ${mediaQueries.mobile} {
      font-size: 42px;
      line-height: 48px;
      letter-spacing: -1px;
    }

    ${mediaQueries.tablet} {
      font-size: 48px;
      line-height: 56px;
      letter-spacing: -1.7px;
    }

    ${mediaQueries.desktop} {
      font-size: 56px;
      line-height: 64px;
      letter-spacing: -1.9px;
    }
  `,

  /* Main section headers, used sparingly. */
  displayAndHeadlineMedium: css`
    font-family: ${FONT_FAMILY_NISTA_GROTESK};
    font-weight: 500;

    ${mediaQueries.mobile} {
      font-size: 40px;
      line-height: 48px;
      letter-spacing: -0.8px;
    }

    ${mediaQueries.tablet} {
      font-size: 42px;
      line-height: 48px;
      letter-spacing: -0.8px;
    }

    ${mediaQueries.desktop} {
      font-size: 48px;
      line-height: 56px;
      letter-spacing: -1px;
    }
  `,

  /* Secondary section headers. */
  displayAndHeadlineSmall: css`
    font-family: ${FONT_FAMILY_NISTA_GROTESK};
    font-weight: 500;

    ${mediaQueries.mobile} {
      font-size: 32px;
      line-height: 40px;
      letter-spacing: -0.6px;
    }

    ${mediaQueries.tablet} {
      font-size: 36px;
      line-height: 44px;
      letter-spacing: -0.7px;
    }

    ${mediaQueries.desktop} {
      font-size: 40px;
      line-height: 48px;
      letter-spacing: -0.8px;
    }
  `,

  /* Secondary section headers.*/
  displayAndHeadlineXtraSmall: css`
    font-family: ${FONT_FAMILY_NISTA_GROTESK};
    font-weight: 500;

    ${mediaQueries.mobile} {
      font-size: 26px;
      line-height: 28px;
      letter-spacing: -0.5px;
    }

    ${mediaQueries.tablet} {
      font-size: 28px;
      line-height: 32px;
      letter-spacing: -0.6px;
    }

    ${mediaQueries.desktop} {
      font-size: 32px;
      line-height: 36px;
      letter-spacing: -0.6px;
    }
  `,

  /* Secondary section headers.*/
  displayAndHeadline2XtraSmallMedium: css`
    font-family: ${FONT_FAMILY_NISTA_GROTESK};
    font-weight: 500;

    ${mediaQueries.mobile} {
      font-size: 20px;
      line-height: 24px;
      letter-spacing: -0.4px;
    }

    ${mediaQueries.tablet} {
      font-size: 20px;
      line-height: 28px;
      letter-spacing: -0.4px;
    }

    ${mediaQueries.desktop} {
      font-size: 24px;
      line-height: 32px;
      letter-spacing: -0.5px;
    }
  `,

  /* Top-level group headers for dashboards. */
  sectionHeaderMedium: css`
    font-family: ${FONT_FAMILY_NISTA_GROTESK};
    font-weight: 500;

    ${mediaQueries.mobile} {
      font-size: 32px;
      line-height: 40px;
      letter-spacing: -0.6px;
    }

    ${mediaQueries.tablet} {
      font-size: 36px;
      line-height: 44px;
      letter-spacing: -0.7px;
    }

    ${mediaQueries.desktop} {
      font-size: 40px;
      line-height: 48px;
      letter-spacing: -0.8px;
    }
  `,

  /* Organizes smaller subsections effectively. */
  subsectionHeaderMedium: css`
    font-family: ${FONT_FAMILY_NISTA_GROTESK};
    font-weight: 500;

    ${mediaQueries.mobile} {
      font-size: 24px;
      line-height: 28px;
      letter-spacing: -0.5px;
    }

    ${mediaQueries.tablet} {
      font-size: 26px;
      line-height: 32px;
      letter-spacing: -0.5px;
    }

    ${mediaQueries.desktop} {
      font-size: 28px;
      line-height: 36px;
      letter-spacing: -0.6px;
    }
  `,

  /* Organizes smaller subsections effectively. */
  subsectionHeaderRegular: css`
    font-family: ${FONT_FAMILY_NISTA_GROTESK};
    font-weight: 400;

    ${mediaQueries.mobile} {
      font-size: 24px;
      line-height: 28px;
      letter-spacing: -0.5px;
    }

    ${mediaQueries.tablet} {
      font-size: 26px;
      line-height: 32px;
      letter-spacing: -0.5px;
    }

    ${mediaQueries.desktop} {
      font-size: 28px;
      line-height: 36px;
      letter-spacing: -0.6px;
    }
  `,

  /* Organizes smaller subsections effectively. */
  subsectionHeaderDense: css`
    font-family: ${FONT_FAMILY_NISTA_GROTESK};
    font-weight: 500;

    ${mediaQueries.mobile} {
      font-size: 24px;
      line-height: 24px;
      letter-spacing: -0.5px;
    }

    ${mediaQueries.tablet} {
      font-size: 26px;
      line-height: 28px;
      letter-spacing: -0.5px;
    }

    ${mediaQueries.desktop} {
      font-size: 28px;
      line-height: 32px;
      letter-spacing: -0.6px;
    }
  `,

  /* Subtitles below prominent headlines. */
  subheaderLargeMedium: css`
    font-family: ${FONT_FAMILY_NISTA_GROTESK};
    font-weight: 500;

    ${mediaQueries.mobile} {
      font-size: 20px;
      line-height: 24px;
      letter-spacing: -0.4px;
    }

    ${mediaQueries.tablet} {
      font-size: 22px;
      line-height: 28px;
      letter-spacing: -0.4px;
    }

    ${mediaQueries.desktop} {
      font-size: 24px;
      line-height: 32px;
      letter-spacing: -0.5px;
    }
  `,

  /* Secondary subtitles in sections. */
  subheaderMediumMedium: css`
    font-family: ${FONT_FAMILY_NISTA_GROTESK};
    font-weight: 500;

    ${mediaQueries.mobile} {
      font-size: 18px;
      line-height: 20px;
      letter-spacing: -0.4px;
    }

    ${mediaQueries.tablet} {
      font-size: 20px;
      line-height: 24px;
      letter-spacing: -0.4px;
    }

    ${mediaQueries.desktop} {
      font-size: 22px;
      line-height: 28px;
      letter-spacing: -0.4px;
    }
  `,

  subheaderSmallMedium: css`
    font-family: ${FONT_FAMILY_NISTA_GROTESK};
    font-weight: 500;

    font-size: 20px;
    line-height: 24px;
    letter-spacing: -0.4px;
  `,

  /* Tertiary subtitles in sections. */
  subsectionHeaderSmallMedium: css`
    font-family: ${FONT_FAMILY_NISTA_GROTESK};
    font-weight: 500;

    font-size: 20px;
    line-height: 24px;
    letter-spacing: -0.4px;
  `,

  /* Larger paragraph text for emphasis. */
  bodyLargeRegular: css`
    font-family: ${FONT_FAMILY_NISTA_GROTESK};
    font-weight: 400;

    font-size: 18px;
    line-height: 26px;
    letter-spacing: -0.4px;
  `,

  /* Larger paragraph text for emphasis. */
  bodyLargeMedium: css`
    font-family: ${FONT_FAMILY_NISTA_GROTESK};
    font-weight: 500;

    font-size: 18px;
    line-height: 26px;
    letter-spacing: -0.4px;
  `,

  /* Standard paragraphs for readability. */
  bodyMediumBaseRegular: css`
    font-family: ${FONT_FAMILY_NISTA_GROTESK};
    font-weight: 400;

    font-size: 16px;
    line-height: 24px;
    letter-spacing: -0.3px;
  `,

  /* Standard paragraphs for readability. */
  bodyMediumBaseMedium: css`
    font-family: ${FONT_FAMILY_NISTA_GROTESK};
    font-weight: 500;

    font-size: 16px;
    line-height: 24px;
    letter-spacing: -0.3px;
  `,

  /* Secondary body text or for tight spaces. */
  bodySmallRegular: css`
    font-family: ${FONT_FAMILY_NISTA_GROTESK};
    font-weight: 400;

    font-size: 14px;
    line-height: 20px;
    letter-spacing: -0.1px;
  `,

  /* Secondary body text or for tight spaces. */
  bodySmallMedium: css`
    font-family: ${FONT_FAMILY_NISTA_GROTESK};
    font-weight: 500;

    font-size: 14px;
    line-height: 20px;
    letter-spacing: -0.1px;
  `,

  /* Equalizes the widths of all numbers in type to make sure lists of numbers align with each other vertically. Aligned numerical or text data in tables, forms, and compact layouts. */
  bodySmallMediumTabular: css`
    font-family: ${FONT_FAMILY_NISTA_GROTESK};
    font-weight: 500;

    font-size: 14px;
    line-height: 20px;
    letter-spacing: -0.3px;
  `,

  /* Small explanatory text or legal disclaimers. */
  bodyExtraSmallRegular: css`
    font-family: ${FONT_FAMILY_NISTA_GROTESK};
    font-weight: 500;

    font-size: 12px;
    line-height: 16px;
    letter-spacing: -0.4px;
  `,

  /* Small explanatory text or legal disclaimers. */
  bodyExtraSmallMedium: css`
    font-family: ${FONT_FAMILY_NISTA_GROTESK};
    font-weight: 400;

    font-size: 12px;
    line-height: 16px;
    letter-spacing: -0.4px;
  `,

  /* Context-setting text below headers, e.g., summaries. */
  introLeadLargeRegular: css`
    font-family: ${FONT_FAMILY_NISTA_GROTESK};
    font-weight: 400;

    ${mediaQueries.mobile} {
      font-size: 20px;
      line-height: 20px;
      letter-spacing: -0.4px;
    }

    ${mediaQueries.tablet} {
      font-size: 20px;
      line-height: 24px;
      letter-spacing: -0.4px;
    }

    ${mediaQueries.desktop} {
      font-size: 24px;
      line-height: 28px;
      letter-spacing: -0.4px;
    }
  `,

  /* Smaller context text, often in compact sections. */
  introLeadMediumRegular: css`
    font-family: ${FONT_FAMILY_NISTA_GROTESK};
    font-weight: 400;

    font-size: 18px;
    line-height: 26px;
    letter-spacing: -0.4px;
  `,

  /* Smaller context text, often in compact sections. */
  introLeadSmallRegular: css`
    font-family: ${FONT_FAMILY_NISTA_GROTESK};
    font-weight: 400;

    font-size: 16px;
    line-height: 24px;
    letter-spacing: -0.3px;
  `,

  /* Emphasized quotes or testimonials. */
  calloutLargeMedium: css`
    font-family: ${FONT_FAMILY_NISTA_GROTESK};
    font-weight: 500;

    ${mediaQueries.mobile} {
      font-size: 26px;
      line-height: 32px;
      letter-spacing: -0.5px;
    }

    ${mediaQueries.tablet} {
      font-size: 28px;
      line-height: 36px;
      letter-spacing: -0.6px;
    }

    ${mediaQueries.desktop} {
      font-size: 32px;
      line-height: 40px;
      letter-spacing: -0.6px;
    }
  `,

  /* Quotes or smaller callouts for highlights. */
  calloutMediumMedium: css`
    font-family: ${FONT_FAMILY_NISTA_GROTESK};
    font-weight: 500;

    ${mediaQueries.mobile} {
      font-size: 24px;
      line-height: 28px;
      letter-spacing: -0.5px;
    }

    ${mediaQueries.tablet} {
      font-size: 26px;
      line-height: 32px;
      letter-spacing: -0.5px;
    }

    ${mediaQueries.desktop} {
      font-size: 28px;
      line-height: 36px;
      letter-spacing: -0.6px;
    }
  `,

  /* Smaller quotes or emphasized statements. */
  calloutSmallMedium: css`
    font-family: ${FONT_FAMILY_NISTA_GROTESK};
    font-weight: 500;

    ${mediaQueries.mobile} {
      font-size: 20px;
      line-height: 24px;
      letter-spacing: -0.4px;
    }

    ${mediaQueries.tablet} {
      font-size: 22px;
      line-height: 28px;
      letter-spacing: -0.4px;
    }

    ${mediaQueries.desktop} {
      font-size: 24px;
      line-height: 32px;
      letter-spacing: -0.5px;
    }
  `,

  /* Smaller quotes or emphasized statements. */
  calloutExtraSmallMedium: css`
    font-family: ${FONT_FAMILY_NISTA_GROTESK};
    font-weight: 500;

    font-size: 20px;
    line-height: 24px;
    letter-spacing: -0.4px;
  `,

  /* Used for quotes, testimonials or highlighting key statements. */
  callout2XtraSmallMedium: css`
    font-family: ${FONT_FAMILY_NISTA_GROTESK};
    font-weight: 500;

    font-size: 18px;
    line-height: 22px;
    letter-spacing: -0.4px;
  `,

  /* Secondary body text or for tight spaces. */
  callout3XtraSmallMedium: css`
    font-family: ${FONT_FAMILY_NISTA_GROTESK};
    font-weight: 500;

    font-size: 16px;
    line-height: 20px;
    letter-spacing: -0.3px;
  `,

  /* Also known as the kicker/eyebrow. Short phrase, to provide additional context to the heading. Sits above headings to give subtle emphasis. */
  overlineLargeRegular: css`
    font-family: ${FONT_FAMILY_NISTA_GROTESK};
    font-weight: 400;

    font-size: 14px;
    line-height: 20px;
    letter-spacing: -0.3px;
  `,

  /* Also known as the kicker/eyebrow. Short phrase, to provide additional context to the heading. Sits above headings to give subtle emphasis. */
  overlineLargeDenseRegular: css`
    font-family: ${FONT_FAMILY_NISTA_GROTESK};
    font-weight: 400;

    font-size: 14px;
    line-height: 18px;
    letter-spacing: -0.3px;
  `,

  /* Also known as the kicker/eyebrow. Short phrase, to provide additional context to the heading. Sits above headings to give subtle emphasis. */
  overlineMediumRegular: css`
    font-family: ${FONT_FAMILY_NISTA_GROTESK};
    font-weight: 400;

    font-size: 12px;
    line-height: 16px;
    letter-spacing: -0.2px;
  `,

  /* Also known as the kicker/eyebrow. Short phrase, to provide additional context to the heading. Sits above headings to give subtle emphasis. */
  overlineSmallRegular: css`
    font-family: ${FONT_FAMILY_NISTA_GROTESK};
    font-weight: 400;

    font-size: 11px;
    line-height: 12px;
    letter-spacing: -0.2px;
  `,

  /* Labels for forms or buttons. */
  labelLargeRegular: css`
    font-family: ${FONT_FAMILY_NISTA_GROTESK};
    font-weight: 400;

    font-size: 16px;
    line-height: 20px;
    letter-spacing: -0.3px;
  `,

  /* Labels for forms or buttons. */
  labelLargeMedium: css`
    font-family: ${FONT_FAMILY_NISTA_GROTESK};
    font-weight: 500;

    font-size: 16px;
    line-height: 20px;
    letter-spacing: -0.3px;
  `,

  /* Less emphasis. Smaller labels for compact elements or metadata. */
  labelMediumRegular: css`
    font-family: ${FONT_FAMILY_NISTA_GROTESK};
    font-weight: 400;

    font-size: 14px;
    line-height: 20px;
    letter-spacing: -0.3px;
  `,

  /* Less emphasis. Smaller labels for compact elements or metadata. */
  labelMediumMedium: css`
    font-family: ${FONT_FAMILY_NISTA_GROTESK};
    font-weight: 500;

    font-size: 14px;
    line-height: 20px;
    letter-spacing: -0.3px;
  `,

  /* Smaller labels for placeholders in input fields. */
  labelMediumRegularDense: css`
    font-family: ${FONT_FAMILY_NISTA_GROTESK};
    font-weight: 400;

    font-size: 14px;
    line-height: 16px;
    letter-spacing: -0.3px;
  `,

  /* Smaller labels for placeholders in input fields. */
  labelMediumMediumDense: css`
    font-family: ${FONT_FAMILY_NISTA_GROTESK};
    font-weight: 500;

    font-size: 14px;
    line-height: 16px;
    letter-spacing: -0.3px;
  `,

  /* Smaller labels for compact elements or metadata. */
  labelSmallRegular: css`
    font-family: ${FONT_FAMILY_NISTA_GROTESK};
    font-weight: 400;

    font-size: 12px;
    line-height: 16px;
    letter-spacing: -0.2px;
  `,

  /* Smaller labels for compact elements or metadata. */
  labelSmallMedium: css`
    font-family: ${FONT_FAMILY_NISTA_GROTESK};
    font-weight: 500;

    font-size: 12px;
    line-height: 16px;
    letter-spacing: -0.2px;
  `,

  /* Smaller labels for compact elements or metadata. */
  labelSmallNeg1Regular: css`
    font-family: ${FONT_FAMILY_NISTA_GROTESK};
    font-weight: 400;

    font-size: 12px;
    line-height: 16px;
    letter-spacing: -0.1px;
  `,

  /* Extra small labels for compact elements, placeholders or metadata. */
  labelXtraSmallRegular: css`
    font-family: ${FONT_FAMILY_NISTA_GROTESK};
    font-weight: 400;

    font-size: 11px;
    line-height: 12px;
    letter-spacing: -0.2px;
  `,

  /* Extra small labels for compact elements, placeholders or metadata. */
  labelXtraSmallMedium: css`
    font-family: ${FONT_FAMILY_NISTA_GROTESK};
    font-weight: 500;

    font-size: 11px;
    line-height: 12px;
    letter-spacing: -0.2px;
  `,

  /* Extra small labels for compact elements, placeholders or metadata. */
  label2XtraSmallRegular: css`
    font-family: ${FONT_FAMILY_NISTA_GROTESK};
    font-weight: 400;

    font-size: 10px;
    line-height: 12px;
    letter-spacing: -0.2px;
  `,

  /* Extra small labels for compact elements, placeholders or metadata. */
  label2XtraSmallMedium: css`
    font-family: ${FONT_FAMILY_NISTA_GROTESK};
    font-weight: 500;

    font-size: 10px;
    line-height: 12px;
    letter-spacing: -0.2px;
  `,

  /* Supportive text for charts, graphs, or images. */
  captionRegular: css`
    font-family: ${FONT_FAMILY_NISTA_GROTESK};
    font-weight: 400;

    font-size: 12px;
    line-height: 16px;
    letter-spacing: -0.2px;
  `,

  /* Supportive text for charts, graphs, or images. */
  captionMedium: css`
    font-family: ${FONT_FAMILY_NISTA_GROTESK};
    font-weight: 500;

    font-size: 12px;
    line-height: 16px;
    letter-spacing: -0.2px;
  `,

  /* Small text that provides quick, additional information on hover or tap. Contextual information displayed on hover. */
  tooltipRegular: css`
    font-family: ${FONT_FAMILY_NISTA_GROTESK};
    font-weight: 400;

    font-size: 10px;
    line-height: 12px;
    letter-spacing: -0.2px;
  `,

  tooltipMedium: css`
    font-family: ${FONT_FAMILY_NISTA_GROTESK};
    font-weight: 500;

    font-size: 10px;
    line-height: 12px;
    letter-spacing: -0.2px;
  `,

  /* For non essential information that doesn’t need much prominence, like footnotes or legal disclaimers. */
  microTextRegular: css`
    font-family: ${FONT_FAMILY_NISTA_GROTESK};
    font-weight: 400;

    font-size: 10px;
    line-height: 12px;
    letter-spacing: -0.2px;
  `,

  /* For non essential information that doesn’t need much prominence, like footnotes or legal disclaimers. */
  microTextMedium: css`
    font-family: ${FONT_FAMILY_NISTA_GROTESK};
    font-weight: 500;

    font-size: 10px;
    line-height: 12px;
    letter-spacing: -0.2px;
  `
};

export const S = {
  Typography: styled.div<{
    variant: TypographyVariant;
    as?: string;
    $color?: ColorToken;
  }>`
    ${TYPOGRAPHY_RESET};
    ${DEFAULT_TYPOGRAPHY_STYLES};
    color: ${({ $color }) => ($color ? COLORS[$color] : 'inherit')};

    ${({ variant }) => TYPOGRAPHY[variant]};
  `
};
